import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Cloud, Lock, Eye, EyeOff, CheckCircle, XCircle, 
  Shield, AlertTriangle, Loader2, Database, Server, Trash2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple encryption for localStorage
const encryptCredentials = (creds) => {
  return btoa(JSON.stringify(creds));
};

const decryptCredentials = (encrypted) => {
  try {
    return JSON.parse(atob(encrypted));
  } catch {
    return null;
  }
};

const AzureConnect = ({ isOpen, onClose, onConnected }) => {
  const [credentials, setCredentials] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    subscription_id: ''
  });
  const [showSecret, setShowSecret] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('azure_creds');
    if (saved) {
      const decrypted = decryptCredentials(saved);
      if (decrypted) {
        setCredentials(decrypted);
        setRememberCredentials(true);
      }
    }
  }, []);

  const handleQuickTest = async () => {
    if (!credentials.tenant_id || !credentials.client_id || 
        !credentials.client_secret || !credentials.subscription_id) {
      toast.error('Please fill in all credentials first');
      return;
    }

    setIsTesting(true);
    setTestResults(null);

    try {
      const response = await axios.post(`${API_URL}/api/azure/quick-test`, credentials);
      setTestResults(response.data);
      
      if (response.data.success) {
        toast.success('All permissions verified!');
      } else {
        toast.warning(response.data.fix_suggestion || 'Some permissions missing');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Test failed';
      setTestResults({
        success: false,
        error: message,
        fix_suggestion: 'Check your credentials'
      });
      toast.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!credentials.tenant_id || !credentials.client_id || 
        !credentials.client_secret || !credentials.subscription_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/azure/connect`, credentials);
      
      if (response.data.success) {
        setConnectionStatus({
          success: true,
          ...response.data.account_info
        });

        if (rememberCredentials) {
          localStorage.setItem('azure_creds', encryptCredentials(credentials));
        } else {
          localStorage.removeItem('azure_creds');
        }

        sessionStorage.setItem('azure_session', encryptCredentials(credentials));

        toast.success('Connected to Azure successfully!');
        
        if (onConnected) {
          onConnected(credentials, response.data.account_info);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({
        success: false,
        error: message
      });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClearSaved = () => {
    localStorage.removeItem('azure_creds');
    sessionStorage.removeItem('azure_session');
    setCredentials({
      tenant_id: '',
      client_id: '',
      client_secret: '',
      subscription_id: ''
    });
    setRememberCredentials(false);
    setConnectionStatus(null);
    toast.success('Saved credentials cleared');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="azure-connect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-500" />
            Connect to Azure
          </DialogTitle>
          <DialogDescription>
            Enter your Azure Service Principal credentials to view cost data from Cost Management.
          </DialogDescription>
        </DialogHeader>

        {/* Security Notice */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Your data stays secure
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Credentials are stored only in your browser. We query Azure directly - 
                <strong> no data is ever stored on our servers.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          {/* Tenant ID */}
          <div className="space-y-2">
            <Label htmlFor="tenant_id">Tenant ID *</Label>
            <Input
              id="tenant_id"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={credentials.tenant_id}
              onChange={(e) => setCredentials({ ...credentials, tenant_id: e.target.value })}
              data-testid="azure-tenant-input"
            />
            <p className="text-xs text-slate-400">
              Found in Azure Portal → Azure Active Directory → Properties
            </p>
          </div>

          {/* Client ID */}
          <div className="space-y-2">
            <Label htmlFor="client_id">Application (Client) ID *</Label>
            <Input
              id="client_id"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={credentials.client_id}
              onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
              data-testid="azure-client-input"
            />
            <p className="text-xs text-slate-400">
              Found in App Registration → Overview
            </p>
          </div>

          {/* Client Secret */}
          <div className="space-y-2">
            <Label htmlFor="client_secret">Client Secret *</Label>
            <div className="relative">
              <Input
                id="client_secret"
                type={showSecret ? 'text' : 'password'}
                placeholder="••••••••••••••••"
                value={credentials.client_secret}
                onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
                data-testid="azure-secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-400"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Subscription ID */}
          <div className="space-y-2">
            <Label htmlFor="subscription_id">Subscription ID *</Label>
            <Input
              id="subscription_id"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={credentials.subscription_id}
              onChange={(e) => setCredentials({ ...credentials, subscription_id: e.target.value })}
              data-testid="azure-subscription-input"
            />
            <p className="text-xs text-slate-400">
              Found in Azure Portal → Subscriptions
            </p>
          </div>

          {/* Required Roles Hint */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Required Role:</strong> Cost Management Reader or Billing Reader
            </p>
            <a 
              href="https://docs.microsoft.com/en-us/azure/cost-management-billing/costs/understand-cost-mgt-data" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-amber-600 hover:underline flex items-center gap-1 mt-1"
            >
              View Azure documentation <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Quick Test Button */}
          <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Quick Permission Test</p>
                <p className="text-xs text-slate-400">Validate Service Principal and roles</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleQuickTest}
                disabled={isTesting || !credentials.tenant_id || !credentials.client_id || !credentials.client_secret || !credentials.subscription_id}
                data-testid="azure-quick-test-btn"
              >
                {isTesting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-1" />
                    Quick Test
                  </>
                )}
              </Button>
            </div>
            
            {/* Test Results */}
            {testResults && (
              <div className={`mt-3 p-2 rounded text-sm ${
                testResults.success 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
                {testResults.success ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      All permissions verified!
                    </div>
                    {testResults.tests && (
                      <div className="text-xs space-y-0.5 ml-5">
                        <p>✓ Azure AD Authentication</p>
                        <p>✓ Subscription Access</p>
                        <p>✓ Cost Management Access</p>
                        {testResults.account_info && (
                          <p className="text-slate-400 dark:text-slate-400">
                            Subscription: {testResults.account_info.subscription_name}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <XCircle className="w-4 h-4" />
                      {testResults.error || 'Test failed'}
                    </div>
                    {testResults.fix_suggestion && (
                      <p className="text-xs ml-5">
                        Fix: {testResults.fix_suggestion}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Remember Credentials */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-400 dark:text-slate-400">
                Remember credentials (browser only)
              </span>
            </label>
            {localStorage.getItem('azure_creds') && (
              <Button variant="ghost" size="sm" onClick={handleClearSaved} className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear saved
              </Button>
            )}
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <div className={`p-3 rounded-lg border ${
              connectionStatus.success 
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {connectionStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    connectionStatus.success ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {connectionStatus.success ? 'Connected Successfully' : 'Connection Failed'}
                  </p>
                  {connectionStatus.success ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 space-y-0.5">
                      <p>Subscription: {connectionStatus.subscription_name}</p>
                      <p>State: {connectionStatus.state}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-red-600">{connectionStatus.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            disabled={isConnecting}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="azure-connect-btn"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4 mr-2" />
                Connect
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hook to get current Azure credentials from session
export const useAzureCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const sessionCreds = sessionStorage.getItem('azure_session');
    if (sessionCreds) {
      const decrypted = decryptCredentials(sessionCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
        return;
      }
    }
    
    const savedCreds = localStorage.getItem('azure_creds');
    if (savedCreds) {
      const decrypted = decryptCredentials(savedCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
      }
    }
  }, []);

  const disconnect = () => {
    sessionStorage.removeItem('azure_session');
    setCredentials(null);
    setIsConnected(false);
  };

  const updateCredentials = (newCreds) => {
    setCredentials(newCreds);
    setIsConnected(true);
    sessionStorage.setItem('azure_session', encryptCredentials(newCreds));
  };

  return { credentials, isConnected, disconnect, updateCredentials };
};

export default AzureConnect;
